import { Box } from '@mui/material';
import * as React from 'react';
import type { JSX } from 'react';

import { colorSystem } from '../../theme';
import { Flexbox } from '../Flexbox';
import { FormItem } from '../forms/FormItem';
import { Text } from '../Text';

type Variant = 'vertical' | 'horizontal' | 'side-by-side' | 'form-item';

/**
 * A property definition.
 */
export interface PropertyDef {
    label: string | JSX.Element;
    value: string | JSX.Element;
    children?: PropertyDef[];
    expandedByDefault?: boolean;
}

/**
 * Renders a list of properties. If the properties can be expanded, a button will be shown
 * to the user to expand the properties. `horizontal` puts them in a row that wraps, as the header of a details page.
 */
export function PropertiesTable({
    variant = 'vertical',
    properties,
}: {
    variant?: Variant;
    properties: PropertyDef[];
}): JSX.Element {
    const entries = properties.filter((property) => property.value !== undefined);

    if (variant === 'form-item') {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {entries.map((property, index) => (
                    <FormItem key={`${property.label}-${index}`} label={property.label}>
                        {property.value}
                    </FormItem>
                ))}
            </Box>
        );
    }

    if (variant === 'side-by-side') {
        return (
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    border: `1px solid ${colorSystem.neutral[2]}`,
                    borderRadius: '4px',
                }}
            >
                {entries.map((property, index) => (
                    <React.Fragment key={`${property.label}-${index}`}>
                        <Text
                            style={{
                                backgroundColor: index % 2 === 1 ? colorSystem.neutral[1] : 'transparent',
                                padding: '4px 8px',
                            }}
                        >
                            {property.label}
                        </Text>
                        <Text
                            style={{
                                backgroundColor: index % 2 === 1 ? colorSystem.neutral[1] : 'transparent',
                                padding: '4px 8px',
                            }}
                        >
                            {property.value}
                        </Text>
                    </React.Fragment>
                ))}
            </Box>
        );
    }

    return (
        <Flexbox
            gap={variant === 'horizontal' ? '12px 32px' : '16px'}
            sx={variant === 'horizontal' ? { flexWrap: 'wrap' } : { flexDirection: 'column' }}
        >
            {entries.map((property, index) => (
                <PropertyLabel key={`${property.label}-${index}`} label={property.label} value={property.value} />
            ))}
        </Flexbox>
    );
}

function PropertyLabel({ label, value }: { label: string | JSX.Element; value?: string | JSX.Element }) {
    return (
        <Flexbox
            gap="2px"
            sx={{
                flexDirection: 'column',
            }}
        >
            <Text variant="body-small" color={colorSystem.neutral[8]}>
                {label}
            </Text>
            <Text variant="h4" color={colorSystem.neutral[9]}>
                {value}
            </Text>
        </Flexbox>
    );
}
